// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2022 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { CustomAppState, xHeaderActions, xHeaderState } from "@hardwoods/App/Store/Reducers";
import { getCommon, getHeader } from "@hardwoods/App/Store/Selectors/CommonState";
import Close from "@hardwoods/XDL/Icons/Close";
import MobileMenu from "@hardwoods/XDL/Icons/MobileMenu";
import { color, isTabletDevice } from "@hardwoods/XDL/Utilities";
import { xScreen } from "@hardwoods/XDL/Utilities/MediaQuery";
import { isTabletRealDevice } from "@hardwoods/XDL/Utilities/Mobile";
import { useEagerLogoLoading } from "@insite/client-framework/Common/EagerLoadingLogo";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper, { getStyledWrapper } from "@insite/client-framework/Common/StyledWrapper";
import setInitialValues from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetInitialValues";
import setNavDrawerIsOpen from "@insite/client-framework/Store/Components/AddressDrawer/Handlers/SetNavDrawerIsOpen";
import { getHomePageUrl } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Icon, { IconWrapper } from "@insite/mobius/Icon";
import { ImgProps } from "@insite/mobius/Img";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps } from "@insite/mobius/Link";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import VisuallyHidden from "@insite/mobius/VisuallyHidden";
import * as cssLinter from "css";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";
import { Dispatch } from "redux";
import { css } from "styled-components";

const enum fields {
    showMenu = "showMenu",
    logoImage = "logoImage",
    isMobileSpecific = "isMobileSpecific",
    mobileSpecificImage = "mobileSpecificImage",
    customCSS = "customCSS",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.showMenu]: boolean;
        [fields.logoImage]: string;
        [fields.isMobileSpecific]: boolean;
        [fields.mobileSpecificImage]: string;
        [fields.customCSS]: string;
    };
    extendedStyles?: LogoStyles;
    externalCustomCSS?: string;
}

export interface LogoStyles {
    wrapper?: InjectableCss;
    menu?: InjectableCss;
    menuTriggerButton?: ClickablePresentationProps;
    logoLink?: LinkPresentationProps;
    /**
     * @deprecated Use img instead
     */
    image?: LazyImageProps;
    img?: ImgProps;
}

export const logoStyles: LogoStyles = {
    wrapper: {
        css: css`
            display: flex;
            width: 200px;
            height: 40px;
            max-width: 100%;
            ${({ sticky }: any) => sticky && `width: 250px;`}

            ${xScreen.lte("md")} {
                width: 250px;
            }

            ${xScreen.lte("sm")} {
                height: 75px;
            }
        `,
    },
    menu: {
        css: css`
            display: flex;
            align-items: center;
            margin-right: 40px;
            ${({ sticky }: any) => !sticky && `display: none;`}
            ${xScreen.lte("md")} {
                display: flex;
            }
            ${xScreen.lte("sm")} {
                margin-right: 15px;
                background-color: ${color("primary")};
                min-width: 75px;
                justify-content: center;

                ${IconWrapper} {
                    color: ${color("common.white")};
                    &,
                    & svg {
                        width: 32px;
                        height: 32px;
                    }
                }
            }
        `,
    },
    image: {
        css: css`
            width: 100%;
            img {
                height: auto;
                width: auto;
                max-height: 100%;
                max-width: 100%;
                align-self: flex-start;
            }
        `,
    },
    menuTriggerButton: {
        css: css`
            ${xScreen.lte("sm")} {
                &.menuLogoComponent {
                    width: 100%;
                    height: 100%;
                    justify-content: center;
                }
            }
        `,
    },
    logoLink: {
        css: css`
            &,
            & span {
                line-height: 0;
                position: static;
                width: 100%;
                height: 100%;
            }

            &:focus {
                outline-width: 0;
            }
        `,
    },
};

const toggleStickyMenu = () => (dispatch: Dispatch<xHeaderActions>, getState: () => xHeaderState) => {
    const state = getState();
    dispatch({ type: "xHeader/stickyMenu", stickyMenu: !state.xHeader.stickyMenu });
};

const mapStateToProps = (state: CustomAppState) => ({
    isSticky: getHeader(state).isSticky,
    stickyMenu: getHeader(state).stickyMenu,
    isTablet: getCommon(state).isTablet,
    homePageLink: getHomePageUrl(state),
});

const mapDispatchToProps = {
    setInitialValues,
    setNavDrawerIsOpen,
    toggleStickyMenu,
};

type Props = OwnProps & WidgetProps & ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps>;

const StyledWrapperX = getStyledWrapper("div") as any;

export const Logo: React.FunctionComponent<Props> = ({
    fields,
    extendedStyles,
    isSticky,
    setNavDrawerIsOpen,
    setInitialValues,
    toggleStickyMenu,
    stickyMenu,
    isTablet,
    homePageLink,
    externalCustomCSS,
}: Props) => {
    const [styles] = React.useState(() => mergeToNew(logoStyles, extendedStyles));
    const isEager = useEagerLogoLoading();

    const customCssWrapper = {
        css: css`
            ${externalCustomCSS || fields.customCSS}
        `,
    };

    const openDrawer = () => {
        const isTablet = isTabletDevice() || isTabletRealDevice(1024);
        if (isTablet) {
            setNavDrawerIsOpen({ navDrawerIsOpen: true });
            setInitialValues({});
        } else {
            toggleStickyMenu();
        }
    };

    return (
        <StyledWrapper {...customCssWrapper}>
            <StyledWrapperX {...styles.wrapper} sticky={isSticky} className="wrapper">
                {fields.showMenu && (
                    <StyledWrapperX sticky={isSticky} {...styles.menu}>
                        <Clickable
                            onClick={openDrawer}
                            {...logoStyles.menuTriggerButton}
                            data-test-selector="expandMobileMenu"
                            className="menuLogoComponent"
                        >
                            <Icon size={24} src={stickyMenu && !isTablet ? Close : MobileMenu} color="text.secondary" />
                            <VisuallyHidden>{translate("menu")}</VisuallyHidden>
                        </Clickable>
                    </StyledWrapperX>
                )}

                <Link href={homePageLink} {...styles.logoLink} className="home-page-link">
                    <LazyImage src={fields.logoImage} altText={translate("Home")} {...styles.image} className="img" />
                </Link>
            </StyledWrapperX>
        </StyledWrapper>
    );
};

const defaultCustomCss = `.wrapper {
}

.home-page-link {
}

.img {
}
`;

const basicTab = {
    displayName: "Basic",
    sortOrder: 0,
};

const advancedTab = {
    displayName: "Advanced",
    sortOrder: 1,
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(Logo),
    definition: {
        group: "Basic",
        icon: "diamond",
        fieldDefinitions: [
            {
                name: fields.showMenu,
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.logoImage,
                displayName: "Logo Image",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "Translatable",
                tab: basicTab,
            },
            {
                name: fields.isMobileSpecific,
                displayName: "Mobile Specific Image",
                editorTemplate: "CheckboxField",
                defaultValue: false,
                fieldType: "General",
                isRequired: true,
                variant: "toggle",
                tab: basicTab,
            },
            {
                name: fields.mobileSpecificImage,
                displayName: "Mobile Logo",
                editorTemplate: "ImagePickerField",
                defaultValue: "",
                fieldType: "Translatable",
                isVisible: widget => widget.fields.isMobileSpecific,
                tab: basicTab,
            },
            {
                name: fields.customCSS,
                displayName: "Custom CSS",
                editorTemplate: "CodeField",
                fieldType: "General",
                tab: advancedTab,
                defaultValue: defaultCustomCss,
                isVisible: (item, shouldDisplayAdvancedFeatures) => !!shouldDisplayAdvancedFeatures,
                validate: value => {
                    if (value === undefined || value === null) {
                        return "";
                    }

                    const result = cssLinter.parse(value, { silent: true });
                    if (result?.stylesheet?.parsingErrors) {
                        // the error output at this time only has room for one line so we just show the first error
                        return result.stylesheet.parsingErrors.length <= 0
                            ? ""
                            : result.stylesheet.parsingErrors.map(error => `${error.reason} on line ${error.line}`)[0];
                    }

                    return "Unable to parse Css";
                },
                options: {
                    mode: "css",
                    lint: true,
                    autoRefresh: true,
                },
            },
        ],
    },
};

export default widgetModule;
